import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useRealtimePresence } from '@/core/realtime';
import { presenceKeys, useCanSeePresence, useOrgPresence } from '../queries';
import type { PresenceDto } from '../types';

/** A leave waits this long, plus jitter, before re-reading: the server caches
 *  the broker's answer for 5 s, and a reload's leave+join should settle first.
 *  Spread out, so every staff tab doesn't ask in the same instant. */
const REFRESH_DELAY_MS = 6_000;
const REFRESH_JITTER_MS = 3_000;

/**
 * Keeps the caller in the org's presence channel — being subscribed is what
 * makes them count as online — and, for staff, keeps the presence list fresh
 * (FE-ADR-068). Renders nothing; mounted once in the header.
 *
 * A join marks that member online at once. A leave is one tab closing, not
 * necessarily the person going offline, so it schedules a re-read; leaves that
 * arrive meanwhile share it. Coming (back) live re-reads too — the list may
 * have been read before this tab's own subscription — and re-reads the
 * caller's own appear-offline choice, which another tab may have changed (the
 * server reconnects every tab after a change).
 */
export function PresenceSync() {
  const canSee = useCanSeePresence();
  const qc = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useOrgPresence();

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const handlers = useMemo(() => {
    if (!canSee) return {};
    const key = presenceKeys.org();
    const refreshSoon = () => {
      if (timer.current) return;
      timer.current = setTimeout(
        () => {
          timer.current = null;
          void qc.invalidateQueries({ queryKey: key }, { cancelRefetch: false });
        },
        REFRESH_DELAY_MS + Math.random() * REFRESH_JITTER_MS,
      );
    };
    return {
      onJoin: (user: string) => {
        const current = qc.getQueryData<PresenceDto>(key);
        // Read while the broker was down, the list knows nobody's status.
        if (!current?.live) {
          refreshSoon();
          return;
        }
        // An older read still in flight must not undo this join.
        void qc.cancelQueries({ queryKey: key });
        const now = new Date().toISOString();
        qc.setQueryData<PresenceDto>(key, {
          ...current,
          members: current.members.map((m) =>
            m.public_id === user ? { ...m, online: true, last_seen_at: now } : m,
          ),
        });
      },
      onLeave: refreshSoon,
    };
  }, [canSee, qc]);

  const live = useRealtimePresence(handlers);
  useEffect(() => {
    if (!live) return;
    void qc.invalidateQueries({ queryKey: presenceKeys.settings() });
    if (canSee) void qc.invalidateQueries({ queryKey: presenceKeys.org() }, { cancelRefetch: false });
  }, [live, canSee, qc]);
  return null;
}
